import React, { useState, useEffect } from 'react';

// Import country map images
import CountryMainlandMap from '../../../../assets/Posters/Country Map/country_mainland_map.png';
import USAAlaskaMap from '../../../../assets/Posters/Country Map/usa_alaska.png';
import MapLocationDialog from './map_location_dialog';

export default function WizardPreviewMapForm({ countryData, detailsData, preferencesData, isCompact = false, onDataChange }) {
    const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
    const [functionAtLocation, setFunctionAtLocation] = useState(detailsData?.functionAtLocation || '');
    const getImageSrc = (imageData, fileName) => {
        if (!imageData) return null;

        // If it's already a full URL (starts with http or https), return it as is
        if (typeof imageData === 'string' && (imageData.startsWith('http://') || imageData.startsWith('https://'))) {
            return imageData;
        }

        // If it's already a data URL, return it as is
        if (typeof imageData === 'string' && imageData.startsWith('data:')) {
            return imageData;
        }

        // If it starts with /, check if it's a valid storage path or a base64 string
        if (typeof imageData === 'string' && imageData.startsWith('/')) {
            if (imageData.includes('/storage/') || imageData.includes('/posters/') || imageData.startsWith('/storage') || imageData.startsWith('/posters')) {
                return imageData;
            }
            const withoutSlash = imageData.substring(1);
            const base64Pattern = /^[A-Za-z0-9+/=]+$/;
            if (base64Pattern.test(withoutSlash) && withoutSlash.length > 50) {
                imageData = withoutSlash;
            } else {
                return imageData;
            }
        }

        // Treat as base64 string and construct data URL
        try {
            let mimeType = 'image/jpeg';
            if (fileName) {
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                if (['jpg', 'jpeg'].includes(fileExtension)) {
                    mimeType = 'image/jpeg';
                } else if (fileExtension === 'png') {
                    mimeType = 'image/png';
                }
            }
            return `data:${mimeType};base64,${imageData}`;
        } catch (e) {
            console.error('Error processing image:', e);
            return null;
        }
    };

    // Update local state when detailsData changes
    useEffect(() => {
        setFunctionAtLocation(detailsData?.functionAtLocation || '');
    }, [detailsData?.functionAtLocation]);

    // Get details data
    const personName = detailsData?.personName || '';
    const businessName = detailsData?.businessName || '';
    const personFunction = detailsData?.personFunction || '';
    const personImage = detailsData?.personImage;
    const personImageFileName = detailsData?.personImageFileName;

    const containerClass = isCompact
        ? 'wizard-preview-map-form wizard-preview-map-form-compact'
        : 'wizard-preview-map-form';

    const personImageSrc = getImageSrc(personImage, personImageFileName);

    // Check if "Show Function Execution in Green" is selected
    const selectedPreferences = preferencesData?.selectedPreferences || [];
    const isGreenSelected = selectedPreferences.includes('show_function_execution_green');
    const isGivenSetSelected = selectedPreferences.includes('show_function_execution_given_set');

    // Get the appropriate map image based on selected country
    const getMapImage = () => {
        const selectedCountry = countryData?.selectedCountry || 'usa_mainland';

        // Option 1: usa_mainland - use country_mainland_map.png
        if (selectedCountry === 'usa_mainland') {
            return CountryMainlandMap;
        }

        // Option 2 & 3: usa_with_alaska_hawaii or other - use usa_alaska.png
        if (selectedCountry === 'usa_with_alaska_hawaii' || selectedCountry === 'other') {
            return USAAlaskaMap;
        }

        // Default fallback
        return CountryMainlandMap;
    };

    const handleLoadAnotherMap = () => {
        setIsMapDialogOpen(true);
    };

    const handleSaveLocation = (location) => {
        // Extract the address from the location object
        const address = location?.address || '';

        // Update local state
        setFunctionAtLocation(address);

        // If onDataChange callback is provided, update the parent component
        if (onDataChange) {
            const updatedDetailsData = {
                ...detailsData,
                functionAtLocation: address
            };
            onDataChange(updatedDetailsData);
        }
    };

    return (
        <div className={containerClass}>
            <div className="map-preview-container">
                <div className="map-preview-wrapper country-map-preview">
                    {/* Map Image */}
                    <img
                        src={getMapImage()}
                        alt="Country Map"
                        className="map-preview-image"
                    />

                    {/* Circular Info Card Overlay */}
                    <div className="map-info-card-overlay">
                        <div className="map-info-card">
                            {/* Business Name at top */}
                            {businessName && (
                                <div className="info-card-business-name">{businessName}</div>
                            )}

                            {/* Images and Names row */}
                            <div className="info-card-images-row">
                                {/* Business Name Block (left side) */}
                                {businessName && (
                                    <div className={`info-card-business-block ${isGreenSelected ? 'green-background' : 'no-background'}`}>
                                        <span className="info-card-business-block-text">{businessName}</span>
                                    </div>
                                )}

                                {/* Person Image */}
                                {personImageSrc ? (
                                    <div className="info-card-image-container">
                                        <img
                                            src={personImageSrc}
                                            alt={personName}
                                            className="info-card-image"
                                        />
                                        <span className="info-card-image-label">{personName}</span>
                                        {personFunction && (
                                            <span className="info-card-image-sublabel">{personFunction}</span>
                                        )}
                                    </div>
                                ) : personName && (
                                    <div className="info-card-image-container">
                                        <div className="info-card-image-placeholder">
                                            <span>{personName?.charAt(0) || '?'}</span>
                                        </div>
                                        <span className="info-card-image-label">{personName}</span>
                                        {personFunction && (
                                            <span className="info-card-image-sublabel">{personFunction}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Using the given set text */}
                            {isGivenSetSelected && (
                                <div className="info-card-given-set-text">
                                    Using the given set: <span className="info-card-given-set-yes">Yes</span>
                                </div>
                            )}
                        </div>

                        {/* Function at location - outside the circle */}
                        {functionAtLocation && (
                            <div className="info-card-location">
                                <div className="info-card-location-title">Function at location</div>
                                <div className="info-card-location-address">{functionAtLocation}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {!isCompact && (
                <div className="map-actions-section">
                    <button
                        className="load-another-map-button"
                        onClick={handleLoadAnotherMap}
                    >
                        Load Another Map
                    </button>
                </div>
            )}

            <MapLocationDialog
                isOpen={isMapDialogOpen}
                onClose={() => setIsMapDialogOpen(false)}
                onSaveLocation={handleSaveLocation}
                initialLocation={detailsData?.functionAtLocation}
            />
        </div>
    );
}
